/**
 * Everything already standing on one day, across every client — what the
 * Schedule and Reschedule dialogs check a new slot against before they let it
 * be saved.
 *
 * Keyed by the day rather than by the dialog, so the two dialogs and two
 * different clients opened on the same date all read one cached answer, and
 * changing the date in a dialog is a fresh question rather than a refetch of
 * the same one.
 *
 * Nothing is asked for until there is a day to ask about: a dialog opens with
 * its date field empty, and a request for "no day" would be a wasted round
 * trip on every open.
 */

import { useQuery } from "@tanstack/react-query";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import { fetchDayAppointments } from "../services/appointmentsService";
import { DAY_CLASH_QUERY_PREFIX } from "../appointmentCaches";
import { dayOf } from "../components/appointmentFields";
import type { ClientAppointment } from "../types/appointments.types";

/** Handed out before there is anything, and the same array every time. */
const NONE: ClientAppointment[] = [];

/** `2026-10-01` — the day as a cache key, and never shown to anybody. */
const keyOf = (day: Date): string => {
  const pad = (part: number) => String(part).padStart(2, "0");
  return `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`;
};

export interface DayAppointmentsState {
  /** Standing appointments on that day, whoever they are with. */
  booked: ClientAppointment[];
  /** True while the day is being read, so a dialog can hold its save. */
  loading: boolean;
  /**
   * Whether the day is known at all. False while it is loading and after a
   * failed read — which is the difference between "this slot is free" and
   * "nobody has checked", and the dialogs say so rather than promising a slot
   * they could not verify.
   */
  known: boolean;
}

/**
 * @param date the date field's own `DD/MM/YYYY`, as typed. An incomplete or
 * impossible one asks nothing.
 */
export function useDayAppointments(date: string): DayAppointmentsState {
  const getToken = useTokenGetter();
  const day = dayOf(date);
  const key = day ? keyOf(day) : "";

  const query = useQuery({
    // Built from the shared prefix so this and the invalidation that
    // marks it stale after a write cannot drift apart.
    queryKey: [...DAY_CLASH_QUERY_PREFIX, key],
    queryFn: ({ signal }) => fetchDayAppointments(getToken, day!, signal),
    enabled: !!day,
    // A diary moves while a dialog is open — somebody else in the practice may
    // book the same hour. Short enough that reopening a dialog re-asks, long
    // enough that typing a date twice does not.
    staleTime: 30_000,
  });

  return {
    booked: query.data ?? NONE,
    loading: !!day && query.isPending,
    known: !!day && query.isSuccess,
  };
}

export default useDayAppointments;
